import PropTypes from 'prop-types';
import swal from 'sweetalert';

import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isEmailVerificationRequiredMessage, isValidToken, setSession } from './utils';
import { mapUserFromApi } from './mapUserFromApi';

import { PATH_AUTH, USER_STATUS } from '../routes/paths';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get('/users/profile');

        const { data } = response;

        const user = mapUserFromApi(data, { includePassword: true, verified: true });

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      const { message, statusCode, error } = err;

      if (
        error === 'Unauthorized' &&
        statusCode === 401 &&
        isEmailVerificationRequiredMessage(message)
      ) {
        // redirect to verification page
        localStorage.clear();
        window.location.href = PATH_AUTH.email_verify;
      }

      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  // SET ACCOUNT DELETION
  const deleteAccount = useCallback(async (id, data) => {
    await axios.delete(`/users/profile`);
  }, []);

  // UPDATEPASSWORD
  const updatePassword = useCallback(async (oldPassword, newPassword) => {
    const userPayload = {
      oldPassword,
      newPassword,
    };

    await axios.post('/users/change-password', userPayload);
  }, []);

  // UPDATEPROFILE
  const updateProfile = useCallback(
    async (data) => {
      const userPayload = {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: parseInt(data.phoneNumber, 10),
        email: data.email,
        address: data.address,
        role: data.role,
      };

      await axios.post('/users/profile', userPayload);

      await initialize();
    },
    [initialize]
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const response = await axios.post('/auth/login', {
      email,
      password,
    });
    const { accessToken, user: apiUser } = response.data;

    const data = apiUser;
    setSession(accessToken);

    if (!(data.status === USER_STATUS.approved)) {
      swal('User Status', 'You are not approved', 'warning');
    }

    const user = mapUserFromApi(data);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  }, []);

  // Google Login
  const googleLogin = useCallback(async (tokenId) => {
    const response = await axios.post('/auth/googleLogin', {
      tokenId,
    });
    const { accessToken, user: apiUser } = response.data;

    const data = apiUser;
    setSession(accessToken);

    if (!(data.status === USER_STATUS.approved)) {
      swal('User Status', 'You are not approved', 'warning');
    }

    const user = mapUserFromApi(data);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (email, password, firstName, lastName, role, phone, address) => {
      // const response =
      await axios.post('/auth/register', {
        email,
        password,
        firstName,
        lastName,
        role,
        phone: parseInt(phone, 10),
        address,
      });
      // const { accessToken, user } = response.data;

      // localStorage.setItem('accessToken', accessToken);
      swal('Registration', 'Registration successful, You can login', 'success');

      // window.location.href = PATH_AUTH.login;

      dispatch({
        type: 'REGISTER',
        payload: {
          // user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      googleLogin,
      register,
      logout,
      initialize,
      updatePassword,
      updateProfile,
      deleteAccount,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      login,
      googleLogin,
      logout,
      register,
      initialize,
      updatePassword,
      deleteAccount,
      updateProfile,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
